import React, { useState } from "react";
import { ArrowRight, Check, Heart, MessageCircle, Shield, Lock, Phone, Calendar, FileText, Flame, Truck, Send } from "lucide-react";
import { C, SERIF, SANS, FAQS, CIRCLES, COMMUNITY_POSTS } from "../lib/constants";
import { Btn, Eyebrow, Accordion, ImgPh } from "../components/Primitives";
import { useSEO } from "../lib/useSEO";
import { SEO } from "../lib/seoRoutes";

export function StoryPage({ go }) {
  useSEO(SEO.story);
  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "64px 28px 72px" }}>
      <Eyebrow>Our Story</Eyebrow>
      <h1 style={{ fontFamily: SERIF, fontSize: 48, color: C.dark, fontWeight: 400,
        margin: "0 0 40px", lineHeight: 1.12 }}>
        We didn't set out to build a brand.<br />
        We set out to build a room women could exhale in.
      </h1>
      <img src="/img/story.png" alt="" style={{ width: "100%", height: 360,
        objectFit: "cover", borderRadius: 16, display: "block" }}
        onError={(e) => { e.target.style.display = "none"; }} />
      <p style={{ fontFamily: SANS, fontSize: 16, color: C.muted, lineHeight: 1.95,
        margin: "40px 0 28px", maxWidth: 700 }}>
        4Ever began with a simple, stubborn question: why does everything made for
        women's bodies feel like it was designed by someone who's never lived inside
        one? We wanted period care that felt like a friend — competent, calm, never
        awkward. So we made it.
      </p>
      <div style={{ background: C.lightBg, borderRadius: 14, padding: "26px 30px",
        marginBottom: 48 }}>
        <div style={{ fontFamily: SANS, fontSize: 10, letterSpacing: 2.5, color: C.muted,
          textTransform: "uppercase", fontWeight: 700, marginBottom: 10 }}>
          Made by people who make things</div>
        <p style={{ fontFamily: SANS, fontSize: 14, color: C.dark, lineHeight: 1.85,
          margin: 0 }}>
          4Ever is made by <strong>Nasibdar Group</strong> — a Mumbai manufacturer with
          25 years of experience producing hygiene products for Indian women and children.
          Every panty is made in-house, to ISO, FDA, CE, BIS, and ISI standards. When we
          say quality, we mean the actual people who make the product are the same people
          who stand behind it.
        </p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48,
        marginBottom: 56 }}>
        <div>
          <Eyebrow>What We Believe</Eyebrow>
          {["Periods are ordinary. Shame is optional.",
            "Wellness isn't a product line. It's a relationship.",
            "Data about your body belongs to you. Full stop.",
            "Community heals what marketing cannot.",
            "The standard we hold ourselves to: would we give this to our best friend?"
          ].map((b, i) => (
            <p key={i} style={{ fontFamily: SERIF, fontSize: 19, color: C.dark,
              lineHeight: 1.5, margin: "0 0 16px", fontWeight: 400 }}>{b}</p>
          ))}
        </div>
        <div>
          <Eyebrow>Where We're Going</Eyebrow>
          <p style={{ fontFamily: SANS, fontSize: 14, color: C.muted, lineHeight: 1.9,
            margin: "0 0 26px" }}>
            Disposable Period Panties are chapter one. We're building a complete women's
            wellness ecosystem — pads, cups, intimate care, fertility, PCOS, postpartum,
            menopause, nutrition, mental health, telehealth — with the same standard:{" "}
            <em style={{ color: C.dark }}>would we give this to our best friend?</em>
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[["25 years", "Of manufacturing behind every panty"],
              ["Made in", "India, with care"],
              ["ISO · FDA", "· CE · BIS · ISI certified"],
              ["OEKO-TEX", "Standard 100 approved"]].map(([n, l]) => (
              <div key={l} style={{ background: C.coralBg, borderRadius: 10,
                padding: "16px 18px" }}>
                <div style={{ fontFamily: SERIF, fontSize: 24, color: C.coral,
                  marginBottom: 3 }}>{n}</div>
                <div style={{ fontFamily: SANS, fontSize: 12, color: C.muted }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ textAlign: "center", borderTop: `1px solid ${C.border}`,
        paddingTop: 44 }}>
        <h2 style={{ fontFamily: SERIF, fontSize: 32, color: C.dark, fontWeight: 400,
          margin: "0 0 20px" }}>Ready to come home?</h2>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <Btn size="lg" onClick={() => go("product")}>
            Shop The Panty <ArrowRight size={15} /></Btn>
          <Btn variant="secondary" size="lg" onClick={() => go("tracker")}>
            Try The Tracker</Btn>
        </div>
      </div>
    </div>
  );
}

export function FAQPage() {
  useSEO(SEO.faq);
  return (
    <div style={{ maxWidth: 780, margin: "0 auto", padding: "56px 28px 72px" }}>
      <Eyebrow>Support</Eyebrow>
      <h1 style={{ fontFamily: SERIF, fontSize: 44, color: C.dark, fontWeight: 400,
        margin: "0 0 40px" }}>Frequently asked questions</h1>
      {FAQS.map(f => <Accordion key={f.q} title={f.q} content={f.a} />)}
      <div style={{ marginTop: 40, padding: "26px 30px", background: C.cream,
        borderRadius: 14, textAlign: "center" }}>
        <div style={{ fontFamily: SERIF, fontSize: 22, color: C.dark,
          marginBottom: 6 }}>Still have questions?</div>
        <div style={{ fontFamily: SANS, fontSize: 13, color: C.muted,
          marginBottom: 18 }}>We're real people and we respond quickly.</div>
        <div style={{ display: "flex", gap: 12, justifyContent: "center",
          flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7,
            fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.green,
            background: C.greenBg, padding: "11px 20px", borderRadius: 6,
            cursor: "pointer" }}>
            <Phone size={14} /> WhatsApp Us</span>
          <a href="mailto:ecom@nasibdar.com" data-testid="faq-contact-email" style={{ display: "inline-flex", alignItems: "center", gap: 7,
            fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.dark, textDecoration: "none",
            border: `1px solid ${C.border}`, background: C.white,
            padding: "11px 20px", borderRadius: 6, cursor: "pointer" }}>
            ecom@nasibdar.com</a>
        </div>
      </div>
    </div>
  );
}

export function CommunityPage() {
  useSEO(SEO.community);
  const [active, setActive] = useState("PCOS");
  const [text, setText] = useState("");
  const [posts, setPosts] = useState([]);
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState("");

  const loadPosts = React.useCallback(async () => {
    try {
      const { communityApi } = await import("../lib/api");
      const { data } = await communityApi.posts(active);
      setPosts(data.posts || []);
    } catch { setPosts([]); }
  }, [active]);

  React.useEffect(() => { loadPosts(); }, [loadPosts]);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true); setErr("");
    try {
      const { communityApi } = await import("../lib/api");
      await communityApi.create(active, text.trim());
      setText("");
      await loadPosts();
    } catch (e) {
      if (e?.response?.status === 401) {
        setErr("Please sign in to post.");
        window.dispatchEvent(new CustomEvent("4ever:open-auth"));
      } else {
        setErr(e?.response?.data?.detail || "Could not post. Try again.");
      }
    } finally { setPosting(false); }
  };
  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "52px 28px 72px" }}>
      <Eyebrow>Community</Eyebrow>
      <h1 style={{ fontFamily: SERIF, fontSize: 42, color: C.dark, fontWeight: 400,
        margin: "0 0 10px" }}>A room where nothing is too much to say.</h1>
      <p style={{ fontFamily: SANS, fontSize: 14, color: C.muted, lineHeight: 1.7,
        margin: "0 0 12px" }}>
        Post anonymously. Moderated by trained women's health professionals.
      </p>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 36 }}>
        {[[<Check size={12} />, "End-to-end anonymous"],
          [<Shield size={12} />, "Professionally moderated"],
          [<Lock size={12} />, "Your identity never shown"]].map(([i, t]) => (
          <span key={t} style={{ display: "flex", alignItems: "center", gap: 5,
            fontFamily: SANS, fontSize: 12, color: C.green }}>{i}{t}</span>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 28 }}>
        <div>
          <div style={{ fontFamily: SANS, fontSize: 9, fontWeight: 700, letterSpacing: 2,
            color: C.muted, textTransform: "uppercase", marginBottom: 10 }}>Circles</div>
          {Object.entries(CIRCLES).map(([n, { count }]) => (
            <button key={n} onClick={() => setActive(n)} style={{ width: "100%",
              textAlign: "left", background: active === n ? C.coralBg : "transparent",
              border: `1px solid ${active === n ? C.coral : "transparent"}`,
              borderRadius: 8, padding: "9px 13px", cursor: "pointer", marginBottom: 4,
              display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontFamily: SANS, fontSize: 13,
                color: active === n ? C.coral : C.dark,
                fontWeight: active === n ? 700 : 400 }}>{n}</span>
              <span style={{ fontFamily: SANS, fontSize: 11,
                color: C.mutedLt }}>{count}</span>
            </button>
          ))}
        </div>
        <div>
          <div style={{ background: C.white, border: `1px solid ${C.border}`,
            borderRadius: 12, padding: 20, marginBottom: 18 }}>
            <div style={{ fontFamily: SANS, fontSize: 12, color: C.muted,
              marginBottom: 10 }}>
              Posting anonymously in <strong style={{ color: C.dark }}>{active}</strong>
            </div>
            <textarea value={text} onChange={e => setText(e.target.value)}
              placeholder="Share something, ask a question, or just say what you're carrying today…"
              style={{ width: "100%", minHeight: 78, padding: "12px 14px",
                border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: SANS,
                fontSize: 13, color: C.dark, lineHeight: 1.7, resize: "vertical",
                outline: "none", background: C.bg, boxSizing: "border-box" }} />
            <div style={{ display: "flex", justifyContent: "space-between",
              alignItems: "center", marginTop: 10 }}>
              <span style={{ fontFamily: SANS, fontSize: 11, color: C.muted }}>
                🔒 Completely anonymous</span>
              <Btn size="sm" onClick={post}>{posting ? "Posting…" : "Post"} <Send size={12} /></Btn>
            </div>
            {err && <div style={{ marginTop: 8, fontFamily: SANS, fontSize: 12, color: C.coral }}>{err}</div>}
          </div>
          {posts.map((p, i) => (
            <PostCard key={p.post_id || i} post={p} active={active} onChange={loadPosts} />
          ))}
        </div>
      </div>
    </div>
  );
}


function PostCard({ post, active, onChange }) {
  const [likes, setLikes] = React.useState(post.likes || 0);
  const [liked, setLiked] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [replies, setReplies] = React.useState([]);
  const [replyText, setReplyText] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const fmt = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return "just now";
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      return d.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
    } catch { return ""; }
  };

  const toggleLike = async () => {
    try {
      const { communityApi } = await import("../lib/api");
      const { data } = await communityApi.like(post.post_id);
      setLikes(data.likes); setLiked(data.liked);
    } catch (e) {
      if (e?.response?.status === 401) window.dispatchEvent(new CustomEvent("4ever:open-auth"));
    }
  };

  const loadReplies = async () => {
    try {
      const { communityApi } = await import("../lib/api");
      const { data } = await communityApi.replies(post.post_id);
      setReplies(data.replies || []);
    } catch {}
  };

  const toggleOpen = async () => {
    const next = !open; setOpen(next);
    if (next) await loadReplies();
  };

  const submitReply = async () => {
    if (!replyText.trim()) return;
    setBusy(true);
    try {
      const { communityApi } = await import("../lib/api");
      await communityApi.reply(post.post_id, replyText.trim());
      setReplyText("");
      await loadReplies();
      onChange && onChange();
    } catch (e) {
      if (e?.response?.status === 401) window.dispatchEvent(new CustomEvent("4ever:open-auth"));
    } finally { setBusy(false); }
  };

  return (
    <div style={{
      background: C.white, border: `1px solid ${C.border}`, borderRadius: 12,
      padding: "18px 20px", marginBottom: 10,
      borderLeft: `3px solid ${post.fresh ? C.coral : "transparent"}`,
    }}>
      <div style={{
        fontFamily: SANS, fontSize: 9, letterSpacing: 1.5,
        color: C.coral, textTransform: "uppercase", marginBottom: 8,
      }}>
        {active} Anonymous · {fmt(post.created_at)}
      </div>
      <p style={{ fontFamily: SANS, fontSize: 14, color: C.dark, lineHeight: 1.8, margin: "0 0 14px" }}>
        {post.text}
      </p>
      <div style={{ display: "flex", gap: 16, fontFamily: SANS, fontSize: 12, color: C.muted }}>
        <button onClick={toggleLike} style={{
          background: "none", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 5,
          color: liked ? C.coral : C.muted, fontFamily: SANS, fontSize: 12,
        }}>
          <Heart size={13} fill={liked ? C.coral : "none"} /> {likes}
        </button>
        <button onClick={toggleOpen} style={{
          background: "none", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 5,
          color: C.muted, fontFamily: SANS, fontSize: 12,
        }}>
          <MessageCircle size={13} /> {open ? "Hide replies" : `${post.replies || 0} replies`}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
          {replies.length === 0 && (
            <div style={{ fontFamily: SANS, fontSize: 12, color: C.mutedLt, marginBottom: 10 }}>
              No replies yet. Be the first to respond kindly.
            </div>
          )}
          {replies.map((r) => (
            <div key={r.reply_id} style={{
              background: r.author === "4ever_team" ? C.coralBg : C.bg,
              borderRadius: 10, padding: "10px 14px", marginBottom: 8,
              borderLeft: r.author === "4ever_team" ? `3px solid ${C.coral}` : "none",
            }}>
              <div style={{
                fontFamily: SANS, fontSize: 10, letterSpacing: 1.5,
                color: r.author === "4ever_team" ? C.coral : C.mutedLt,
                textTransform: "uppercase", fontWeight: 700, marginBottom: 4,
              }}>
                {r.author === "4ever_team" ? "— 4Ever Team" : "Anonymous"} · {fmt(r.created_at)}
              </div>
              <div style={{ fontFamily: SANS, fontSize: 13, color: C.dark, lineHeight: 1.7 }}>
                {r.text}
              </div>
            </div>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <input value={replyText} onChange={e => setReplyText(e.target.value)}
              placeholder="Reply kindly, anonymously" style={{
                flex: 1, padding: "10px 12px", border: `1px solid ${C.border}`,
                borderRadius: 8, fontFamily: SANS, fontSize: 13, background: C.bg, outline: "none",
              }} />
            <Btn size="sm" onClick={submitReply}>{busy ? "Sending…" : "Reply"}</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

export function TrackerPage({ go }) {
  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "56px 28px 72px" }}>
      <div style={{ textAlign: "center", marginBottom: 48 }}>
        <Eyebrow>Your Cycle, Gently Understood</Eyebrow>
        <h1 style={{ fontFamily: SERIF, fontSize: 46, color: C.dark, fontWeight: 400,
          margin: "0 0 14px" }}>A tracker that guides, never nags.</h1>
        <p style={{ fontFamily: SANS, fontSize: 15, color: C.muted, lineHeight: 1.8,
          maxWidth: 560, margin: "0 auto 28px" }}>
          Log how you feel in 20 seconds a day. We learn your rhythm, predict your
          period, and time your delivery so your comfort kit arrives before you need it.
        </p>
        <Btn size="lg">Open Your Tracker <ArrowRight size={15} /></Btn>
        <div style={{ fontFamily: SANS, fontSize: 12, color: C.mutedLt, marginTop: 12 }}>
          Free to use · No purchase required · 4ever.co.in/tracker
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20,
        marginBottom: 48 }}>
        {[
          { i: <Calendar size={20} />, t: "Learns your real cycle",
            d: "Not everyone runs on 28 days. After 2 cycles of tracking, predictions adapt to your actual rhythm — including irregular and PCOS cycles." },
          { i: <Truck size={20} />, t: "Syncs your delivery",
            d: "Subscribers get their comfort kit 2 days before their predicted period. Automatically. No Indian brand does this — only 4Ever." },
          { i: <FileText size={20} />, t: "Share with your doctor",
            d: "Export a 3-month cycle summary as a PDF. Especially useful for PCOS and endometriosis consultations, where history changes the conversation." },
        ].map(f => (
          <div key={f.t} style={{ background: C.white, border: `1px solid ${C.border}`,
            borderRadius: 14, padding: 26 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: C.coralBg,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: C.coral, marginBottom: 14 }}>{f.i}</div>
            <div style={{ fontFamily: SERIF, fontSize: 19, color: C.dark,
              marginBottom: 8 }}>{f.t}</div>
            <div style={{ fontFamily: SANS, fontSize: 13, color: C.muted,
              lineHeight: 1.7 }}>{f.d}</div>
          </div>
        ))}
      </div>
      {/* Live preview mock */}
      <div style={{ background: C.white, border: `1px solid ${C.border}`,
        borderRadius: 18, padding: 32, maxWidth: 640, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between",
          alignItems: "center", marginBottom: 16 }}>
          <span style={{ background: "#F7E8E0", color: "#8A3E22", fontFamily: SANS,
            fontSize: 11, fontWeight: 700, padding: "4px 12px",
            borderRadius: 14 }}>Day 5 · Menstrual phase</span>
          <span style={{ display: "flex", alignItems: "center", gap: 5, fontFamily: SANS,
            fontSize: 11, fontWeight: 700, color: C.coral }}>
            <Flame size={12} />14-day streak</span>
        </div>
        <div style={{ fontFamily: SERIF, fontSize: 30, color: C.dark,
          marginBottom: 6 }}>Rest is the work.</div>
        <div style={{ fontFamily: SANS, fontSize: 13, color: C.muted, marginBottom: 18,
          lineHeight: 1.6 }}>
          Next period in 23 days · Comfort kit ships Aug 5, synced to your cycle
        </div>
        <div style={{ fontFamily: SANS, fontSize: 12, fontWeight: 600, color: C.dark,
          marginBottom: 8 }}>How are you, really?</div>
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 8 }}>
          {["None", "Light", "Medium", "Heavy"].map((o, i) => (
            <span key={o} style={{ padding: "7px 16px", borderRadius: 20,
              border: `1.5px solid ${i === 2 ? C.coral : C.border}`,
              background: i === 2 ? C.coralBg : C.white, fontFamily: SANS, fontSize: 12,
              color: i === 2 ? C.coral : C.muted, fontWeight: i === 2 ? 700 : 400 }}>{o}</span>
          ))}
        </div>
        <div style={{ fontFamily: SANS, fontSize: 11, color: C.mutedLt }}>
          Flow · Mood · Energy · Sleep · Movement · Cravings · Symptoms — 20 seconds, done.
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start",
        padding: "16px 22px", borderRadius: 12, border: `1px solid ${C.border}`,
        maxWidth: 640, margin: "32px auto 0" }}>
        <Lock size={14} color={C.muted} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontFamily: SANS, fontSize: 12, color: C.muted, lineHeight: 1.65 }}>
          <strong style={{ color: C.dark }}>Your data stays yours. Always.</strong>{" "}
          Everything you log is encrypted, stored on Indian servers, never shared or
          sold. Export or permanently delete your history anytime.
        </div>
      </div>
    </div>
  );
}

export function AccountPage({ go }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [orders, setOrders] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [panel, setPanel] = React.useState(null); // "subscription" | "rewards" | "wellness" | "notifications" | "privacy"

  const refresh = React.useCallback(async () => {
    try {
      const { accountApi } = await import("../lib/api");
      const [acct, ords] = await Promise.all([accountApi.get(), accountApi.orders()]);
      setData(acct.data);
      setOrders(ords.data.orders || []);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" });
    } catch { return iso; }
  };

  const reorder = async (order) => {
    const { cartApi } = await import("../lib/api");
    try {
      // Server catalog reprices at checkout, so just push the same items back.
      await cartApi.set(order.items);
      // Refetch + open drawer via App-level listener.
      window.dispatchEvent(new CustomEvent("4ever:open-cart"));
    } catch { alert("Could not reorder."); }
  };

  const downloadInvoice = async (order) => {
    try {
      const { API } = await import("../lib/api");
      const token = localStorage.getItem("4ever_token");
      const res = await fetch(`${API}/orders/${order.order_id}/invoice.pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `4ever-invoice-${order.order_id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Could not download invoice right now."); }
  };

  const cards = data ? [
    { key: "subscription", title: "Next delivery",
      value: data.next_delivery ? fmtDate(data.next_delivery) : "Set up tracker",
      sub: data.next_delivery ? "Synced to your cycle" : "Add your cycle to unlock sync",
      action: "Manage subscription" },
    { key: "orders", title: "Recent orders",
      value: `${data.orders_count} order${data.orders_count === 1 ? "" : "s"}`,
      sub: "This year", action: "View history" },
    { key: "rewards", title: "Bloom points",
      value: `${data.points} pts`,
      sub: `${data.tier} tier · earn 20% back on every order`,
      action: "See rewards" },
    { key: "wellness", title: "Wellness profile",
      value: data.cycle?.cycleLen ? `${data.cycle.cycleLen}-day cycle` : "Not set",
      sub: data.cycle?.periodLen ? `${data.cycle.periodLen}-day period` : "Track to personalise",
      action: "Update profile" },
    { key: "notifications", title: "Notifications",
      value: "Only what matters", sub: "No spam, ever", action: "Manage" },
    { key: "privacy", title: "Privacy & data",
      value: "Encrypted", sub: "Export or delete anytime", action: "Privacy centre" },
  ] : [];
  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: "52px 28px 72px" }}>
      <Eyebrow>Welcome Back</Eyebrow>
      <h1 style={{ fontFamily: SERIF, fontSize: 40, color: C.dark, fontWeight: 400,
        margin: "0 0 8px" }}>Hello, friend.</h1>
      <p style={{ fontFamily: SANS, fontSize: 14, color: C.muted, margin: "0 0 32px",
        lineHeight: 1.7 }}>
        Your quiet corner of 4Ever. Everything you've saved, subscribed to, and
        shared — softly organised.
      </p>
      <div style={{ display: "flex", gap: 12, marginBottom: 36 }}>
        <Btn onClick={() => go("tracker")}>Open Tracker</Btn>
        <Btn variant="secondary" onClick={() => go("product")}>Shop The Panty</Btn>
      </div>
      {loading ? (
        <div style={{ fontFamily: SANS, fontSize: 13, color: C.muted }}>Loading your account…</div>
      ) : (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
        {cards.map((c) => (
          <div key={c.key} style={{ background: C.white, border: `1px solid ${C.border}`,
            borderRadius: 14, padding: 22 }}>
            <div style={{ fontFamily: SANS, fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600 }}>{c.title}</div>
            <div style={{ fontFamily: SERIF, fontSize: 21, color: C.dark, marginBottom: 2 }}>{c.value}</div>
            <div style={{ fontFamily: SANS, fontSize: 12, color: C.muted, marginBottom: 14 }}>{c.sub}</div>
            <button onClick={() => {
              if (c.key === "wellness") { go("tracker"); return; }
              setPanel(panel === c.key ? null : c.key);
            }} style={{
              background: "none", border: "none", cursor: "pointer",
              fontFamily: SANS, fontSize: 12, color: C.coral, textDecoration: "underline", padding: 0,
            }}>{c.action}</button>
          </div>
        ))}
      </div>
      )}

      {/* Detail panels */}
      {panel === "subscription" && <SubscriptionPanel onClose={() => setPanel(null)} onSaved={refresh} />}
      {panel === "rewards" && <RewardsPanel balance={data?.points || 0} tier={data?.tier} tierNext={data?.tier_next} onClose={() => setPanel(null)} />}
      {panel === "notifications" && <NotificationsPanel onClose={() => setPanel(null)} />}
      {panel === "privacy" && <PrivacyPanel onClose={() => setPanel(null)} onWiped={() => window.location.reload()} />}

      {/* Order history */}
      {!loading && orders.length > 0 && (
        <div style={{ marginTop: 44 }}>
          <h2 style={{ fontFamily: SERIF, fontSize: 24, color: C.dark, fontWeight: 400, margin: "0 0 18px" }}>
            Order history
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {orders.map(o => (
              <div key={o.order_id} style={{
                background: C.white, border: `1px solid ${C.border}`, borderRadius: 12,
                padding: "16px 20px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                  cursor: "pointer" }} onClick={() => setSelected(selected === o.order_id ? null : o.order_id)}>
                  <div>
                    <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.dark }}>
                      #{o.order_id}
                    </div>
                    <div style={{ fontFamily: SANS, fontSize: 11, color: C.muted }}>
                      {fmtDate(o.created_at)} · {o.items.length} item{o.items.length === 1 ? "" : "s"} · {o.status}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <span style={{ fontFamily: SANS, fontSize: 15, fontWeight: 700, color: C.dark }}>₹{o.total}</span>
                    <span style={{ fontFamily: SANS, fontSize: 12, color: C.coral, textDecoration: "underline" }}>
                      {selected === o.order_id ? "Hide" : "View"}
                    </span>
                  </div>
                </div>
                {selected === o.order_id && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
                    {o.items.map((it, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "center", padding: "8px 0", fontFamily: SANS, fontSize: 13, color: C.dark }}>
                        <span>Disposable Period Panties · Size {it.size} · Pack of 5 × {it.qty}
                          {it.sub && <span style={{ color: C.green, marginLeft: 6, fontSize: 11 }}>· Subscription</span>}
                        </span>
                        <span style={{ fontWeight: 700 }}>₹{it.price * it.qty}</span>
                      </div>
                    ))}
                    <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                      <Btn size="sm" onClick={() => reorder(o)}>Reorder</Btn>
                      <Btn size="sm" variant="secondary" onClick={() => downloadInvoice(o)}>Download invoice</Btn>
                    </div>
                    <div style={{ marginTop: 10, fontFamily: SANS, fontSize: 11, color: C.mutedLt }}>
                      Ships from Mumbai · Track number available after dispatch
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DetailWrap({ title, onClose, children }) {
  return (
    <div style={{ marginTop: 28, background: C.white, border: `1px solid ${C.border}`,
      borderRadius: 14, padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h2 style={{ fontFamily: SERIF, fontSize: 22, color: C.dark, fontWeight: 400, margin: 0 }}>{title}</h2>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer",
          fontFamily: SANS, fontSize: 12, color: C.muted, textDecoration: "underline" }}>Close</button>
      </div>
      {children}
    </div>
  );
}

function SubscriptionPanel({ onClose, onSaved }) {
  const [sub, setSub] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => { (async () => {
    const { accountApi } = await import("../lib/api");
    try { const { data } = await accountApi.getSub(); setSub(data); }
    catch {} finally { setLoading(false); }
  })(); }, []);
  const save = async (patch) => {
    const next = { ...(sub || {}), ...patch };
    setSub(next);
    const { accountApi } = await import("../lib/api");
    await accountApi.putSub(next);
    onSaved && onSaved();
  };
  if (loading) return <DetailWrap title="Manage subscription" onClose={onClose}><div style={{ fontFamily: SANS, fontSize: 13, color: C.muted }}>Loading…</div></DetailWrap>;
  return (
    <DetailWrap title="Manage subscription" onClose={onClose}>
      <p style={{ fontFamily: SANS, fontSize: 13, color: C.muted, margin: "0 0 18px", lineHeight: 1.7 }}>
        Your comfort kit arrives 2 days before your predicted period. Pause, skip, or cancel any time — no penalties.
      </p>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 18 }}>
        <span style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.dark, minWidth: 120 }}>Status</span>
        <button onClick={() => save({ active: !(sub?.active) })} style={{
          background: sub?.active ? C.green : C.mutedLt, color: "#fff", border: "none",
          padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontFamily: SANS, fontSize: 12, fontWeight: 600,
        }}>{sub?.active ? "Active — pause" : "Paused — resume"}</button>
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 18 }}>
        <span style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.dark, minWidth: 120 }}>Frequency</span>
        <div style={{ display: "flex", gap: 8 }}>
          {[["monthly", "Every month"], ["six_weeks", "Every 6 weeks"]].map(([k, l]) => (
            <button key={k} onClick={() => save({ frequency: k })} style={{
              padding: "8px 14px", borderRadius: 6, cursor: "pointer",
              border: `1.5px solid ${sub?.frequency === k ? C.coral : C.border}`,
              background: sub?.frequency === k ? C.coralBg : C.white, fontFamily: SANS, fontSize: 12,
              color: sub?.frequency === k ? C.coral : C.dark, fontWeight: 600,
            }}>{l}</button>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.dark, minWidth: 120 }}>Size</span>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["S-M", "M-L", "L-XL", "XL-XXL", "XXL-XXXL"].map(s => (
            <button key={s} onClick={() => save({ size: s })} style={{
              padding: "6px 12px", borderRadius: 6, cursor: "pointer",
              border: `1.5px solid ${sub?.size === s ? C.coral : C.border}`,
              background: sub?.size === s ? C.coralBg : C.white, fontFamily: SANS, fontSize: 12,
              color: sub?.size === s ? C.coral : C.dark, fontWeight: 600,
            }}>{s}</button>
          ))}
        </div>
      </div>
    </DetailWrap>
  );
}

function RewardsPanel({ balance, tier, tierNext, onClose }) {
  const [ledger, setLedger] = React.useState([]);
  React.useEffect(() => { (async () => {
    const { accountApi } = await import("../lib/api");
    try { const { data } = await accountApi.pointsLedger(); setLedger(data.ledger || []); } catch {}
  })(); }, []);
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-IN", { month: "short", day: "numeric" }); }
    catch { return ""; }
  };
  const labelFor = (reason) => {
    if (reason === "welcome") return "Welcome to 4Ever";
    if (reason?.startsWith("tracker_log")) return "Daily tracker log";
    if (reason?.startsWith("order:")) return `Order ${reason.split(":")[1]}`;
    if (reason?.startsWith("redeem:")) return `Redeemed on ${reason.split(":")[1]}`;
    return reason || "Adjustment";
  };
  return (
    <DetailWrap title="Bloom points" onClose={onClose}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
        <div style={{ background: C.coralBg, borderRadius: 12, padding: 18 }}>
          <div style={{ fontFamily: SANS, fontSize: 11, color: C.muted, marginBottom: 4 }}>Your balance</div>
          <div style={{ fontFamily: SERIF, fontSize: 32, color: C.coral, fontWeight: 400 }}>{balance} pts</div>
          <div style={{ fontFamily: SANS, fontSize: 12, color: C.muted, marginTop: 4 }}>1 pt = ₹1 at checkout</div>
        </div>
        <div style={{ background: C.cream, borderRadius: 12, padding: 18 }}>
          <div style={{ fontFamily: SANS, fontSize: 11, color: C.muted, marginBottom: 4 }}>Tier</div>
          <div style={{ fontFamily: SERIF, fontSize: 22, color: C.dark }}>{tier}</div>
          <div style={{ fontFamily: SANS, fontSize: 12, color: C.muted, marginTop: 4 }}>
            {tierNext > 0 ? `${tierNext} pts to Gold` : "Highest tier"}
          </div>
        </div>
      </div>
      <div style={{ fontFamily: SANS, fontSize: 12, color: C.muted, marginBottom: 8, lineHeight: 1.7 }}>
        You earn <strong style={{ color: C.dark }}>50 pts</strong> when you join,{" "}
        <strong style={{ color: C.dark }}>10 pts</strong> for each daily tracker log, and{" "}
        <strong style={{ color: C.dark }}>20% back</strong> on every order.
      </div>
      <div style={{ fontFamily: SANS, fontSize: 12, letterSpacing: 2, color: C.muted,
        textTransform: "uppercase", fontWeight: 600, margin: "18px 0 10px" }}>Ledger</div>
      {ledger.length === 0 && (
        <div style={{ fontFamily: SANS, fontSize: 13, color: C.mutedLt }}>Nothing here yet.</div>
      )}
      {ledger.map((e, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between",
          padding: "10px 0", borderBottom: `1px solid ${C.border}`, fontFamily: SANS, fontSize: 13 }}>
          <span style={{ color: C.dark }}>{labelFor(e.reason)}</span>
          <span style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <span style={{ color: C.mutedLt, fontSize: 11 }}>{fmt(e.created_at)}</span>
            <span style={{ color: e.delta >= 0 ? C.green : C.coral, fontWeight: 700 }}>
              {e.delta >= 0 ? `+${e.delta}` : e.delta} pts
            </span>
          </span>
        </div>
      ))}
    </DetailWrap>
  );
}

function NotificationsPanel({ onClose }) {
  const [prefs, setPrefs] = React.useState(null);
  React.useEffect(() => { (async () => {
    const { accountApi } = await import("../lib/api");
    try { const { data } = await accountApi.getNotifs(); setPrefs(data); } catch {}
  })(); }, []);
  const toggle = async (key) => {
    const next = { ...(prefs || {}), [key]: !(prefs?.[key]) };
    setPrefs(next);
    const { accountApi } = await import("../lib/api");
    await accountApi.putNotifs(next);
  };
  if (!prefs) return <DetailWrap title="Notifications" onClose={onClose}><div style={{ fontFamily: SANS, fontSize: 13, color: C.muted }}>Loading…</div></DetailWrap>;
  const rows = [
    ["order_updates", "Order updates", "Confirmations, dispatch, delivery"],
    ["period_reminders", "Period reminders", "Two days before your next predicted period"],
    ["sunday_letter", "The Sunday Letter", "One quiet email each week"],
    ["marketing", "Occasional offers", "Rare — only when it matters"],
  ];
  return (
    <DetailWrap title="Notifications" onClose={onClose}>
      {rows.map(([k, t, s]) => (
        <div key={k} style={{ display: "flex", justifyContent: "space-between",
          alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
          <div>
            <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.dark }}>{t}</div>
            <div style={{ fontFamily: SANS, fontSize: 12, color: C.muted }}>{s}</div>
          </div>
          <button onClick={() => toggle(k)} style={{
            width: 44, height: 24, borderRadius: 12, position: "relative",
            border: "none", cursor: "pointer",
            background: prefs[k] ? C.coral : C.mutedLt,
          }}>
            <span style={{
              position: "absolute", top: 3, left: prefs[k] ? 22 : 3,
              width: 18, height: 18, borderRadius: "50%", background: "#fff",
              transition: "left 0.2s",
            }} />
          </button>
        </div>
      ))}
    </DetailWrap>
  );
}

function PrivacyPanel({ onClose, onWiped }) {
  const [confirming, setConfirming] = React.useState(false);
  const wipe = async () => {
    const { accountApi } = await import("../lib/api");
    try {
      await accountApi.wipeAll();
      alert("Your data has been erased. You'll be signed out.");
      localStorage.removeItem("4ever_token");
      onWiped && onWiped();
    } catch { alert("Could not erase data. Please contact us."); }
  };
  return (
    <DetailWrap title="Privacy centre" onClose={onClose}>
      <p style={{ fontFamily: SANS, fontSize: 13, color: C.muted, lineHeight: 1.8, margin: "0 0 18px" }}>
        Your data stays yours. Cycle logs, orders, community posts, and preferences are encrypted and stored on Indian servers.
      </p>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <Btn variant="secondary" size="sm" onClick={async () => {
          const { accountApi } = await import("../lib/api");
          const [acct, ords, prefs, sub] = await Promise.all([
            accountApi.get(), accountApi.orders(), accountApi.getNotifs(), accountApi.getSub(),
          ]);
          const bundle = { account: acct.data, orders: ords.data, notifications: prefs.data, subscription: sub.data };
          const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url; a.download = "4ever-my-data.json"; a.click();
          URL.revokeObjectURL(url);
        }}>Download my data (JSON)</Btn>
      </div>
      <div style={{ background: C.coralBg, borderRadius: 10, padding: "14px 18px" }}>
        <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.dark, marginBottom: 6 }}>
          Erase everything
        </div>
        <div style={{ fontFamily: SANS, fontSize: 12, color: C.muted, marginBottom: 10, lineHeight: 1.6 }}>
          Removes your cart, tracker logs, community posts, orders, points and preferences. Cannot be undone.
        </div>
        {confirming ? (
          <div style={{ display: "flex", gap: 8 }}>
            <Btn size="sm" onClick={wipe}>Yes, erase everything</Btn>
            <Btn size="sm" variant="secondary" onClick={() => setConfirming(false)}>Cancel</Btn>
          </div>
        ) : (
          <Btn size="sm" variant="secondary" onClick={() => setConfirming(true)}>Erase all my data</Btn>
        )}
      </div>
    </DetailWrap>
  );
}

export function SizeGuidePage({ go }) {
  useSEO(SEO.sizeguide);
  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "56px 28px 72px" }}>
      <Eyebrow>Fit</Eyebrow>
      <h1 style={{ fontFamily: SERIF, fontSize: 42, color: C.dark, fontWeight: 400,
        margin: "0 0 14px" }}>Size guide</h1>
      <p style={{ fontFamily: SANS, fontSize: 14, color: C.muted, lineHeight: 1.8,
        margin: "0 0 32px" }}>
        Full-coverage brief cut with a soft elasticated waist. The fit is forgiving —
        when between sizes, size up for overnight comfort.
      </p>
      <div style={{ borderRadius: 12, overflow: "hidden",
        border: `1px solid ${C.border}` }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: SANS,
          fontSize: 13 }}>
          <thead>
            <tr style={{ background: C.cream }}>
              {["Size", "Waist size (inches)", "Hip size (inches)"].map(h => (
                <th key={h} style={{ padding: "13px 16px", textAlign: "left",
                  color: C.dark, fontWeight: 700, fontSize: 12 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[["S-M", "22–28", "32–40"], ["M-L", "28–32", "36–44"],
              ["L-XL", "32–38", "38–46"], ["XL-XXL", "38–42", "42–50"],
              ["XXL-XXXL", "42–52", "46–54"]].map((r, i) => (
              <tr key={r[0]} style={{ background: i % 2 ? C.bg : C.white }}>
                {r.map((c, j) => (
                  <td key={j} style={{ padding: "13px 16px", color: j === 0 ? C.dark : C.muted,
                    fontWeight: j === 0 ? 700 : 400,
                    borderTop: `1px solid ${C.border}` }}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 24, background: C.coralBg, borderRadius: 12,
        padding: "16px 20px", fontFamily: SANS, fontSize: 13, color: C.dark,
        lineHeight: 1.7 }}>
        <strong>Note:</strong>
        <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
          <li>Size up for a comfortable fit.</li>
          <li>Absorbency remains the same for all sizes.</li>
        </ul>
      </div>
      <div style={{ marginTop: 20, fontFamily: SANS, fontSize: 12, color: C.muted, lineHeight: 1.7 }}>
        <strong style={{ color: C.dark }}>How to measure:</strong> Waist — around the narrowest
        part of your torso. Hip — around the fullest part, about 8 inches below your waist.
        Still unsure? WhatsApp us a question — we answer fit queries daily.
      </div>
      <div style={{ marginTop: 28 }}>
        <Btn onClick={() => go("product")}>Back to The Panty <ArrowRight size={14} /></Btn>
      </div>
    </div>
  );
}
